import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Icon, Input, Message, Modal, Progress, Segment } from "semantic-ui-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";

import type {
	CreateDataLayerUploadRequest,
	DataLayerTypeSelectOption,
	DataLayerUpload,
	UUID,
} from "../../../../apis/types/dataLayerTypes";
import {
	ApiError,
	createVectorDataLayerUploadWithProgress,
	vectorDataLayerUploadsQueryOptions,
} from "../../../../apis/dataLayerApi";
import { AerialPlotResponse } from "../../../../apis/types";
import SimpleOkCancelModal from "../../../Lumber/SimpleOkCancelModal";
import { useAuth0 } from "../../../../auth/auth0";

import styles from "./styles.module.css";

interface VectorUploadRowProps {
	clientId: UUID;
	// the dataLayer Id this upload links to
	dataLayerId: UUID;
	// options for building DDL of DataLayerTypes
	dataLayerTypeSelectOptions: DataLayerTypeSelectOption[];
	// DataLayerUploads record for displaying upload metadata and URLs.
	upload: DataLayerUpload;
	validationMsg?: string;
	// conditional flags
	isDeleting: boolean;
	isConfiguring: boolean;
	isDisabled: boolean;
	// callbacks
	onBeforeModify: (action: () => void) => void;
	onDelete: (upload: DataLayerUpload) => void | Promise<void>;
	onUploadResponse: (pendingId: UUID, newId: UUID, response: AerialPlotResponse<GeoJSON.FeatureCollection>) => void;
	onConfigVectorUpload: (upload: DataLayerUpload) => void;
	onOpenDownloader: (upload: DataLayerUpload) => void;
}

const DATE_FORMAT = "MM/DD/YYYY";

enum StatusIcon {
	Config = "pencil",
	Minus = "minus",
}

/**
 * Vector Data Layer Upload Form Control: Upload/Download management for one DataLayerUpload record.
 */
const VectorUploadRow = ({
	clientId,
	dataLayerId,
	dataLayerTypeSelectOptions,
	upload,
	validationMsg,
	onBeforeModify,
	onDelete,
	onUploadResponse,
	onConfigVectorUpload,
	onOpenDownloader,
	isDeleting,
	isConfiguring,
	isDisabled,
}: VectorUploadRowProps) => {
	const { getTokenSilently } = useAuth0() as { getTokenSilently: () => Promise<string> };

	/*
	 * States
	 */
	const fileInputRef = useRef<HTMLInputElement | null>(null);
	const abortControllerRef = useRef<AbortController | null>(null);
	const [selectedDataLayerType, setSelectedDataLayerType] = useState<UUID | null>(null);
	const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false);
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
	const [uploadProgress, setUploadProgress] = useState<number | null>(null);
	const [isUploading, setIsUploading] = useState(false);
	const [isProcessing, setIsProcessing] = useState(false);

	/*
	 * Queries and Mutation
	 */
	const queryClient = useQueryClient();
	const { mutateAsync: vectorDataLayerUploadMutationAsync } = useMutation({
		mutationFn: createVectorDataLayerUploadWithProgress,
		onSuccess: () => {
			// refresh list of uploads for our data layer id
			const { queryKey } = vectorDataLayerUploadsQueryOptions(getTokenSilently, clientId, dataLayerId);
			void queryClient.invalidateQueries({ queryKey });
		},
		onSettled: () => {
			setUploadProgress(null);
			setIsUploading(false);
			setIsProcessing(false);
			abortControllerRef.current = null;
		},
	});

	/*
	 * Effects
	 */

	// extract name from the rawDataUrlPath because it was not modeled as a separate column in the
	// DataLayerUploads table
	useEffect(() => {
		if (upload.rawDataUrlPath) {
			const pathParts = upload.rawDataUrlPath.split("-");
			const fileName = pathParts.slice(5).join("-");
			setSelectedFileName(fileName);
		}
		// else the setSelectedFileName will get set if user selects through the file browser.
	}, [upload]);

	/*
	 * Memoization
	 */

	// change row status from "remove" to "config" mark, after upload completion.
	const statusIcon = useMemo(() => {
		if (upload.rawDataUrlPath) {
			return StatusIcon.Config;
		}
		return StatusIcon.Minus;
	}, [upload]);

	const statusIconTooltip = useMemo(() => {
		if (statusIcon === StatusIcon.Config) {
			return "Configure vector data layer";
		}
		if (statusIcon === StatusIcon.Minus) {
			return "Remove new upload record";
		}
		return "";
	}, [statusIcon]);

	const isUploaded = useMemo(() => {
		return !isUploading && (!!upload.rawDataUrlPath || !!upload.urlPath);
	}, [isUploading, upload]);

	const confirmDeleteMsg = useMemo(
		() => `Vector data upload will be deleted permanently: ${selectedFileName}`,
		[selectedFileName],
	);

	const progressModalHeader = useMemo(() => (isProcessing ? "Processing" : "Uploading"), [isProcessing]);

	const progressModalMsg = useMemo(
		() =>
			isProcessing
				? `Please wait, processing ${selectedFile?.name}...`
				: `Please wait, uploading ${selectedFile?.name} (${selectedFile?.size} bytes, ${uploadProgress?.toFixed(0)}%)`,
		[isProcessing, selectedFile, uploadProgress],
	);

	const dateUploadedFmt = useMemo(
		() => moment.utc(upload.createdDateTimeUtc).local().format(DATE_FORMAT),
		[upload.createdDateTimeUtc],
	);

	/*
	 * Callbacks
	 */
	const handleFileBrowse = useCallback(() => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	}, []);

	const handleFileSelected = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const fileData = event.target.files[0];
			setSelectedFileName(fileData.name);
			setSelectedFile(fileData);
		}
	}, []);

	const handleDeleteModalCancel = useCallback(() => {
		setDeleteModalIsOpen(false);
	}, []);

	const handleDeleteModalOk = useCallback(async () => {
		setDeleteModalIsOpen(false);
		await onDelete(upload);
	}, [upload, onDelete]);

	const handleDeleteModalRequest = useCallback(() => {
		setDeleteModalIsOpen(true);
	}, []);

	const handleUploadProgress = useCallback((event: ProgressEvent) => {
		if (event.lengthComputable && event.total > 0) {
			const percent = (event.loaded / event.total) * 100;
			setUploadProgress(percent);
			if (percent >= 100) {
				setIsProcessing(true);
			}
		}
	}, []);

	/**
	 * Send POST request with file upload. Issue callback to parent component with response payload:
	 * preprocessed GeoJson.
	 */
	const handleUploadRequest = useCallback(
		async (event: React.MouseEvent) => {
			event.preventDefault();
			if (!selectedDataLayerType || !selectedFile) {
				throw Error("Invalid upload request");
			}
			setIsUploading(true);
			abortControllerRef.current = new AbortController();
			const request: CreateDataLayerUploadRequest = {
				dataLayerId,
				dataLayerTypeId: selectedDataLayerType,
				file: selectedFile,
			};
			try {
				const uploadResponse = await vectorDataLayerUploadMutationAsync({
					getAccessToken: getTokenSilently,
					clientId,
					request,
					onProgress: handleUploadProgress,
					signal: abortControllerRef.current.signal,
				});
				// Runtime API response includes properties.id on the FeatureCollection, not modeled in GeoJSON types
				const newId = (uploadResponse.data as unknown as { properties: { id: string } }).properties.id;
				if (!newId) {
					throw Error("Invalid response data (no uuid)");
				}
				const pendingId = upload.id;
				onUploadResponse(pendingId, newId, uploadResponse);
			} catch (error: unknown) {
				if (error instanceof DOMException && error.name === "AbortError") {
					toast.info("Upload/processing cancelled by user.");
					return;
				}
				if (error instanceof ApiError && error.body?.messages) {
					const messages = error.body.messages as string[];
					toast.error(messages.join("; "));
				} else if (error instanceof Error) {
					toast.error(error.message);
				}
				console.error(error);
			}
		},
		[
			selectedFile,
			selectedDataLayerType,
			dataLayerId,
			getTokenSilently,
			clientId,
			handleUploadProgress,
			onUploadResponse,
			upload.id,
			vectorDataLayerUploadMutationAsync,
		],
	);

	const handleCancelUpload = useCallback(() => {
		if (abortControllerRef.current) {
			abortControllerRef.current.abort();
		}
		setIsUploading(false);
	}, []);

	const handleDataLayerTypeChange = useCallback(
		(
			_event: React.SyntheticEvent<HTMLElement>,
			data: { value?: string | number | boolean | (string | number | boolean)[] },
		) => {
			setSelectedDataLayerType((data.value as UUID) ?? null);
		},
		[],
	);

	const handleActionButton = useCallback(
		(evt: React.MouseEvent) => {
			evt.preventDefault();
			if (statusIcon === StatusIcon.Minus) {
				// re-use the business logic (same as modal confirmation ui flow)
				void handleDeleteModalOk();
			} else if (statusIcon === StatusIcon.Config) {
				// callback for config modal ui flow
				onConfigVectorUpload(upload);
			} else {
				console.error(`unknown action status ${String(statusIcon)}`);
			}
		},
		[statusIcon, upload, onConfigVectorUpload, handleDeleteModalOk],
	);

	/*
	 * Rendering
	 */

	return (
		<>
			<Form error={!!validationMsg} className={styles.vectorDataLayerCtrl}>
				<Message error content={validationMsg} icon="info circle" className={styles.validationMsg} />
				<Form.Group className={styles.uploadRowGrid}>
					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellAction}`}>
						<Tooltip placement="top" overlay={statusIconTooltip}>
							{/* span here so tooltip will always display even when button is disabled */}
							<span className={styles.tooltipContent}>
								{/* Button displaying either warning icon, minus (remove), or pencil (configure). */}
								<Button
									icon={statusIcon}
									disabled={isDeleting || isDisabled || isConfiguring}
									onClick={handleActionButton}
									color={validationMsg ? "red" : undefined}
									size="small"
								/>
							</span>
						</Tooltip>
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellDownload}`}>
						<Tooltip placement="top" overlay="Download vector data files">
							<span className={styles.tooltipContent}>
								<Button
									onClick={() => onOpenDownloader(upload)}
									icon="download"
									disabled={isDisabled || (!upload.stdDataSasUrl && !upload.rawDataSasUrl) || isDeleting}
									size="small"
								/>
							</span>
						</Tooltip>
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellType}`}>
						{isUploaded ? (
							<div className={`${styles.centerText} ${styles.numZones} ${styles.uploadTypeReadonly}`}>
								{dataLayerTypeSelectOptions.find((dlt) => dlt.value === upload.dataLayerTypeId)?.text}
								{upload.vectorDataNumZones ? ` (${upload.vectorDataNumZones} zone)` : ""}
							</div>
						) : (
							<Form.Select
								id="form-select-data-layer-type"
								placeholder="Select type of data layer"
								options={dataLayerTypeSelectOptions}
								disabled={isDisabled || isUploading}
								value={selectedDataLayerType || upload.dataLayerTypeId}
								multiple={false}
								onChange={handleDataLayerTypeChange}
							/>
						)}
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellBrowse}`}>
						{!isUploaded && (
							<Tooltip placement="top" overlay="Find upload source asset on local filesystem">
								<span className={styles.tooltipContent}>
									<Button
										content="Browse local..."
										labelPosition="left"
										icon="folder open solid"
										disabled={isDisabled || isUploading || isDeleting}
										onClick={handleFileBrowse}
										size="small"
									/>
								</span>
							</Tooltip>
						)}
					</Form.Field>

					<input
						id="vectorDataLayerUpload"
						ref={fileInputRef}
						type="file"
						accept=".geojson,application/geo+json,application/json,.json,application/x-gpkg,.gpkg,application/zip,.zip"
						hidden
						onChange={handleFileSelected}
					/>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellDelete}`}>
						<Tooltip placement="top" overlay="Delete vector data layer upload">
							<span className={styles.tooltipContent}>
								<Button
									onClick={handleDeleteModalRequest}
									icon={isDeleting ? "spinner" : "trash"}
									disabled={isDisabled || !isUploaded || isDeleting}
								/>
							</span>
						</Tooltip>
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellFile}`}>
						<Input disabled value={selectedFileName ?? ""} />
					</Form.Field>

					<Form.Field className={`${styles.uploadCell} ${styles.uploadCellFinal}`}>
						{isUploaded && (
							<div className={`${styles.centerText} ${styles.uploadDateLabel}`}>Date Uploaded: {dateUploadedFmt}</div>
						)}
						{!isUploaded && (
							<Button
								primary={!!selectedFileName && !!selectedDataLayerType}
								type="submit"
								disabled={!selectedFileName || !selectedDataLayerType || isUploading || isDeleting}
								onClick={(evt: React.MouseEvent) => onBeforeModify(() => void handleUploadRequest(evt))}
								compact
							>
								<Icon name="cloud upload" />
								Upload
							</Button>
						)}
					</Form.Field>
				</Form.Group>
			</Form>
			<Modal open={isUploading} closeOnDimmerClick={false} closeOnEscape={false}>
				<Modal.Header>{progressModalHeader}</Modal.Header>
				<Modal.Content>
					<Modal.Description>
						<Segment basic>{progressModalMsg}</Segment>
						{uploadProgress !== null && <Progress active percent={uploadProgress} />}
					</Modal.Description>
				</Modal.Content>
				<Modal.Actions>
					<Button onClick={handleCancelUpload}>Cancel</Button>
				</Modal.Actions>
			</Modal>

			<SimpleOkCancelModal
				headerMsg="Confirm Delete"
				bodyMsg={confirmDeleteMsg}
				open={deleteModalIsOpen}
				onOk={() => void handleDeleteModalOk()}
				onCancel={handleDeleteModalCancel}
			/>
		</>
	);
};

export default VectorUploadRow;
